import { LocalNotifications } from '@capacitor/local-notifications';
import { App } from '@capacitor/app';

class BadgeManager {
  constructor() {
    this.badgeCount = 0;
    this.isInitialized = false;
    this.notificationId = 999;
    this.init();
  }

  async init() {
    try {
      // Request notification permissions
      const permission = await LocalNotifications.requestPermissions();
      console.log('🔔 Notification permission:', permission);
      
      // Create notification channel for Android
      if (permission.display === 'granted') {
        await this.createNotificationChannel();
      }
      
      this.isInitialized = true;
    } catch (error) {
      console.log('❌ Notification permissions not available:', error);
    }
  }

  async createNotificationChannel() {
    try {
      console.log('📱 Creating notification channel for badge support');
      
      // Create notification channel for Android
      await LocalNotifications.createChannel({
        id: 'chefnet_chat',
        name: 'ChefNet Chat',
        description: 'Chat notifications from ChefNet',
        importance: 4, // High importance
        visibility: 1, // Public visibility
        sound: 'default',
        vibration: true
      });
      
      console.log('✅ Notification channel created successfully');
    } catch (error) {
      console.log('❌ Failed to create notification channel:', error);
    }
  }

  async incrementBadge() {
    try {
      if (!this.isInitialized) {
        await this.init();
      }
      
      this.badgeCount++;
      console.log('🔢 Incrementing badge count to:', this.badgeCount);
      
      // Show a new notification with the updated badge count
      await this.showBadgeNotification();
    } catch (error) {
      console.log('❌ Failed to increment badge:', error);
    }
  }
  
  async handleNewNotification() {
    try {
      if (!this.isInitialized) {
        await this.init();
      }
      
      this.badgeCount++;
      console.log('🔔 New notification received, badge count:', this.badgeCount);
      
      // Update both notification badge and app icon badge
      await this.showBadgeNotification();
    } catch (error) {
      console.log('❌ Failed to handle new notification:', error);
    }
  }

  async clearBadge() {
    try {
      this.badgeCount = 0;
      console.log('🧹 Clearing badge count');
      
      // Cancel the persistent notification
      await LocalNotifications.cancel({ notifications: [{ id: this.notificationId }] });
      
      // Also cancel any other notifications
      await LocalNotifications.cancel({ notifications: [{ id: 1000 }] });
      await LocalNotifications.cancel({ notifications: [{ id: 1001 }] });
      
      // Clear app icon badge
      await this.setAppIconBadge();
      
      console.log('✅ Badge cleared successfully');
    } catch (error) {
      console.log('❌ Failed to clear badge:', error);
    }
  }

  async showBadgeNotification() {
    try {
      console.log('📱 Showing badge notification with count:', this.badgeCount);
      
      // Cancel any existing notification first
      await LocalNotifications.cancel({ notifications: [{ id: this.notificationId }] });
      
      // Create a new notification with the badge count
      await LocalNotifications.schedule({
        notifications: [{
          id: this.notificationId,
          title: 'ChefNet',
          body: this.badgeCount === 1 ? 'You have 1 new message' : `You have ${this.badgeCount} new messages`,
          badge: this.badgeCount,
          sound: null,
          silent: true,
          ongoing: true, // Make it persistent so it stays in notification tray
          autoCancel: false,
          channelId: 'chefnet_chat',
          schedule: { at: new Date(Date.now() + 100) }
        }]
      });
      
      // Set app icon badge
      await this.setAppIconBadge();
      
      console.log('✅ Badge notification scheduled successfully');
    } catch (error) {
      console.log('❌ Failed to show badge notification:', error);
    }
  }
  
  async setAppIconBadge() {
    try {
      // Set the app icon badge using Capacitor App plugin
      if (this.badgeCount > 0) {
        await App.setBadgeNumber({ badge: this.badgeCount });
        console.log('📱 App icon badge set to:', this.badgeCount);
      } else {
        await App.clearBadge();
        console.log('📱 App icon badge cleared');
      }
    } catch (error) {
      console.log('❌ Failed to set app icon badge:', error);
      // Fallback to notification badge
      console.log('🔄 Falling back to notification badge only');
    }
  }

  async showTestNotification() {
    try {
      console.log('🧪 Showing test notification');
      
      await LocalNotifications.schedule({
        notifications: [{
          id: 1000,
          title: 'Test Notification',
          body: 'This is a test notification to verify badge support',
          badge: 1,
          sound: 'default',
          channelId: 'chefnet_chat',
          schedule: { at: new Date(Date.now() + 1000) }
        }]
      });
      
      console.log('✅ Test notification scheduled');
    } catch (error) {
      console.log('❌ Failed to show test notification:', error);
    }
  }

  getBadgeCount() {
    return this.badgeCount;
  }
  
  async syncBadgeWithNotifications(notificationCount) {
    try {
      console.log('🔄 Syncing badge with notification count:', notificationCount);
      
      if (notificationCount !== this.badgeCount) {
        this.badgeCount = notificationCount;
        await this.setAppIconBadge();
        
        if (this.badgeCount > 0) {
          await this.showBadgeNotification();
        } else {
          await this.clearBadge();
        }
        
        console.log('✅ Badge synced successfully to:', this.badgeCount);
      }
    } catch (error) {
      console.log('❌ Failed to sync badge:', error);
    }
  }

  async requestPermissions() {
    try {
      const result = await LocalNotifications.requestPermissions();
      console.log('🔔 Permission request result:', result);
      return result;
    } catch (error) {
      console.log('❌ Failed to request permissions:', error);
      return null;
    }
  }
}

// Create a singleton instance
const badgeManager = new BadgeManager();
export default badgeManager; 